import { getTransactionParam } from "components/Layout/Campaign/Common/campaignHelpers";
import { PickRequired } from "types/global";
import { KeyValuePair } from "utilities/objects";
import { DataItemType, DataType } from "./EffectSearch";

export const filterElements = (searchQuery: string, items: DataItemType[]) => {
  return [...items].filter(
    (item) =>
      item.label?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.code?.toLowerCase().includes(searchQuery.toLowerCase()),
  );
};

export const mapData = (data: DataType[]) => {
  const mappedData: PickRequired<DataItemType, "code" | "codeRegexMatcher">[] =
    [];

  data.forEach(({ items }) => {
    items.forEach((item) => {
      if (item.attributes) {
        mappedData.push(...item.attributes);
      } else {
        mappedData.push(
          item as PickRequired<DataItemType, "code" | "codeRegexMatcher">,
        );
      }
    });
  });

  return mappedData;
};

const splitElementsBySpace = (expressions: string[], data: DataType[]) => {
  const parsed: string[] = [];

  expressions.forEach((exp) => {
    const isChipsValue = !!findChipsData(exp, data);

    if (isChipsValue) {
      parsed.push(exp);
    } else {
      parsed.push(...exp.split(" "));
    }
  });

  return parsed;
};

export function findChipsData(chipsCode: string, data: DataType[]) {
  const dataAdapted = mapData(data);

  return dataAdapted.find(
    (variable) =>
      variable.codeRegexMatcher &&
      chipsCode.match(variable.codeRegexMatcher) !== null,
  );
}

export function generateChildren(expression: string, data: DataType[]): any[] {
  const expressionSplittedByMarkersArray = splitElementsBySpace(
    expression
      .split(/<#>(.*?)<\/#>/g)
      .map((e) =>
        e
          .trim()
          .replaceAll(/\s+(?=[^[\]]*\])/g, "")
          .replaceAll(/\s+(?=[^[\]]*\))/g, "")
          .replace("<#>", "")
          .replace("</#>", ""),
      )
      .filter((elem) => elem.length > 0 && elem !== " "),

    data,
  ).filter((elem) => elem.length > 0);
  const childrenArray: any = [];

  expressionSplittedByMarkersArray.forEach((expressionPart: string) => {
    const trimedExpression = expressionPart.trim();

    if (findChipsData(trimedExpression, data)) {
      const chipsData = findChipsData(trimedExpression, data);

      childrenArray.push({
        type: "chips",
        code: expressionPart,
        label: chipsData?.label,
        parameterType: chipsData?.parameterType,
      });
    } else {
      childrenArray.push({ text: expressionPart });
    }
  });

  return childrenArray;
}

export function parseValue(
  expression: string,
  data: DataType[],
  returnNonChipsValue?: boolean,
): any[] {
  const dataAdapted = mapData(data);
  //split entire expression by markers
  const expressionSplittedByMarkersArray = expression
    .split(/<span .*?>(.*?)<\/span>/g)
    .filter((elem) => elem.length > 0 && elem !== " ");

  const childrenArray: any = [];

  expressionSplittedByMarkersArray.forEach((expressionPart: string) => {
    const isChipsValue = dataAdapted.find(({ codeRegexMatcher }) =>
      expressionPart.match(codeRegexMatcher),
    );

    if (isChipsValue && !returnNonChipsValue) {
      childrenArray.push(`<#>${expressionPart}</#>`);
    } else {
      const formattedExpressions = expressionPart
        .replaceAll(/<.*?>/g, " ") // replace opening tag
        .replaceAll(/<\/.*?>/g, " ") // replace closing tag
        .replaceAll("&lt;", "<")
        .replaceAll("&gt;", ">")
        .replaceAll("&nbsp;", " ")
        .replaceAll(/&.*?;/g, "") // replace &specialChar;
        .trim();

      childrenArray.push(formattedExpressions);
    }
  });

  return childrenArray.join(" ");
}

export const prepareCleanExpression = (expression: string) =>
  expression?.replaceAll("<#>", "").replaceAll("</#>", "");

export const getCriterionIntialValues = (
  value: string,
  expressionData?: DataItemType,
) => {
  const params = getTransactionParam(value);

  const initialValues: {
    productBrand: string[];
    productName: string[];
    productCategory: string[];
    productCustomAttribute: KeyValuePair[];
  } = {
    productBrand: [],
    productName: [],
    productCategory: [],
    productCustomAttribute: [],
  };

  if (
    expressionData?.parameterType === "itemsCriteria" &&
    Array.isArray(params)
  ) {
    params.forEach((criteria) => {
      switch (criteria.criterion) {
        case "productBrand":
          initialValues.productBrand = criteria.values as string[];
          break;
        case "productName":
          initialValues.productName = criteria.values as string[];
          break;
        case "productCategory":
          initialValues.productCategory = criteria.values as string[];
          break;
        case "productCustomAttribute":
          initialValues.productCustomAttribute =
            criteria.values as KeyValuePair[];
          break;
      }
    });
  }

  return initialValues;
};
